import { useState } from "react";
import { Page, useCurrent, Grid } from "./common";
import { PageHead } from "../components/ui";
import { Icon } from "../components/Icons";
import { fmtDate, relative, daysUntil } from "../lib/format";
import { actions } from "../lib/store";
import { useCan } from "../lib/auth";
import { BRIEF_MILESTONES, milestonesReady } from "../lib/milestones";
import type { ScheduleEvent } from "../lib/types";

const KIND: Record<ScheduleEvent["kind"], { color: string; label: string }> = {
  milestone: { color: "var(--accent)", label: "Milestone" },
  meeting: { color: "var(--sky)", label: "Meeting" },
  site: { color: "var(--sage)", label: "Site" },
  deadline: { color: "var(--danger)", label: "Deadline" },
  internal: { color: "var(--ink-3)", label: "Internal" },
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Schedule() {
  const p = useCurrent();
  const [ym, setYm] = useState({ y: 2026, m: 6 }); // July 2026 (0-idx)
  if (!p) return null;

  const first = new Date(ym.y, ym.m, 1);
  const startPad = (first.getDay() + 6) % 7; // Mon-first
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(startPad).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const monthName = first.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const eventsOn = (d: number) => p.schedule.filter((e) => { const dt = new Date(e.date); return dt.getFullYear() === ym.y && dt.getMonth() === ym.m && dt.getDate() === d; });
  const shift = (n: number) => setYm((s) => { const d = new Date(s.y, s.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  const upcoming = [...p.schedule].filter((e) => daysUntil(e.date) >= -1).sort((a, b) => +new Date(a.date) - +new Date(b.date));

  return (
    <Page>
      <PageHead kicker={`${p.name} · Programme`} title="Schedule" />
      <MilestoneBar p={p} />
      <Grid cols="1.6fr 1fr" gap={22}>
        {/* calendar */}
        <div className="card pad reveal">
          <div className="row between" style={{ marginBottom: 18 }}>
            <h3 className="serif" style={{ fontSize: 22 }}>{monthName}</h3>
            <div className="row" style={{ gap: 8 }}>
              <button className="iconbtn" style={{ width: 32, height: 32 }} onClick={() => shift(-1)}><Icon.back width={16} /></button>
              <button className="iconbtn" style={{ width: 32, height: 32 }} onClick={() => shift(1)}><Icon.arrow width={16} /></button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
            {DOW.map((d) => <div key={d} className="kicker" style={{ fontSize: 10, textAlign: "center", paddingBottom: 4 }}>{d}</div>)}
            {cells.map((d, i) => {
              const evs = d ? eventsOn(d) : [];
              const isToday = d === 10 && ym.y === 2026 && ym.m === 6;
              return (
                <div key={i} style={{ minHeight: 74, borderRadius: 10, padding: 6, border: "1px solid " + (isToday ? "var(--accent)" : "var(--line)"), background: d ? (isToday ? "var(--accent-soft)" : "var(--paper)") : "transparent" }}>
                  {d && <div className="mono" style={{ fontSize: 11, color: isToday ? "var(--accent-ink)" : "var(--ink-3)", fontWeight: isToday ? 700 : 400 }}>{d}</div>}
                  <div className="col" style={{ gap: 3, marginTop: 3 }}>
                    {evs.slice(0, 2).map((e) => (
                      <div key={e.id} title={e.title} style={{ fontSize: 10, lineHeight: 1.25, padding: "2px 5px", borderRadius: 5, background: KIND[e.kind].color, color: "#fff", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{e.title}</div>
                    ))}
                    {evs.length > 2 && <div className="dim" style={{ fontSize: 9 }}>+{evs.length - 2}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="row wrap" style={{ gap: 14, marginTop: 16 }}>
            {Object.entries(KIND).map(([k, v]) => <span key={k} className="row dim" style={{ gap: 6, fontSize: 11.5 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: v.color }} />{v.label}</span>)}
          </div>
        </div>

        {/* agenda */}
        <div className="card pad reveal">
          <div className="kicker" style={{ marginBottom: 14 }}>Upcoming</div>
          <div className="col" style={{ gap: 2 }}>
            {upcoming.map((e) => (
              <div key={e.id} className="row" style={{ gap: 12, padding: "12px 0", borderBottom: "1px solid var(--line)" }}>
                <div style={{ textAlign: "center", width: 44, flexShrink: 0 }}>
                  <div className="serif" style={{ fontSize: 22, lineHeight: 1 }}>{new Date(e.date).getDate()}</div>
                  <div className="kicker" style={{ fontSize: 9 }}>{new Date(e.date).toLocaleDateString("en-US", { month: "short" })}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{e.title}</div>
                  <div className="row" style={{ gap: 8, marginTop: 3 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 3, background: KIND[e.kind].color }} />
                    <span className="dim" style={{ fontSize: 12 }}>{KIND[e.kind].label} · {relative(e.date)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Grid>
    </Page>
  );
}

/* Milestones are planned on the Task Mind Map — that's where the studio sets
   the dates — and the dates come back here to fill the calendar. */
function MilestoneBar({ p }: { p: any }) {
  const canEdit = useCan("stages");
  const [busy, setBusy] = useState<"push" | "pull" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const ready = milestonesReady(p);
  const mm = p.mindmap;
  const planned = Object.keys(mm?.milestones ?? {}).length;
  const onCalendar = (p.schedule ?? []).filter((e: ScheduleEvent) => BRIEF_MILESTONES.some((m) => m.title === e.title)).length;

  async function push() {
    setBusy("push"); setMsg(null);
    const r = await actions.pushMilestonesToMindmap(p.id);
    setMsg(r.ok ? `${r.count} milestones are on the board — set the dates there, then pull them back.` : r.reason ?? "Couldn't send them.");
    setBusy(null);
  }
  async function pull() {
    setBusy("pull"); setMsg(null);
    const r = await actions.pullMilestoneDates(p.id);
    setMsg(!r.ok ? (r.reason ?? "Couldn't read the board.")
      : (r.added || r.moved?.length)
        ? `Calendar updated — ${r.added} added${r.moved?.length ? `, ${r.moved.length} moved (${r.moved.join(", ")})` : ""}.`
        : "Already in step with the board.");
    setBusy(null);
  }

  return (
    <div className="card pad reveal" style={{ marginBottom: 18 }}>
      <div className="row between wrap" style={{ gap: 12 }}>
        <div>
          <div className="row" style={{ gap: 8 }}>
            <span className="kicker">Programme milestones</span>
            {planned > 0 && <span className="badge ok" style={{ fontSize: 9 }}><span className="dot" />on the mind map</span>}
            {!ready && <span className="badge" style={{ fontSize: 9 }}>brief not approved</span>}
          </div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 4, maxWidth: 620 }}>
            {!ready
              ? <>Milestones open up once the brief is approved — until then the target still moves.</>
              : planned
                ? <>{planned} milestones on the board · {onCalendar} dated here. Set the dates on the mind map, then pull them into this calendar.</>
                : <>The brief is approved, so the programme can be planned. Push the milestones onto the mind map, date them there, and they'll fill this calendar.</>}
          </div>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {mm?.url && <a className="btn ghost sm" href={mm.url} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={14} />Open board</a>}
          {canEdit && ready && (
            <button className="btn soft sm" onClick={push} disabled={!!busy}>
              <Icon.send width={14} />{busy === "push" ? "Sending…" : planned ? "Re-send milestones" : "Plan on mind map"}
            </button>
          )}
          {canEdit && planned > 0 && (
            <button className="btn accent sm" onClick={pull} disabled={!!busy}>
              <Icon.swap width={14} />{busy === "pull" ? "Reading…" : "Pull dates in"}
            </button>
          )}
        </div>
      </div>
      {msg && <div className="dim" style={{ fontSize: 12.5, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)" }}>{msg}</div>}
    </div>
  );
}
